/**
 * The one-click install of an agent (docs/refactor/51, N3): a button that runs the agent's official
 * installer, the last line it printed while it works, and — once installed — the sign-in, which is
 * the next thing to do. Codex needs Node: without it, the button says so and offers Node's page.
 */

import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Download, Loader2 } from 'lucide-react'
import { agentInstall, onAgentInstall } from '../lib/agentInstall'
import { openExternal } from '../lib/desktop'
import { useI18n } from '../lib/i18n'
import { detectAgents, type DetectedAgent, type LocalAgentId } from '../lib/localAgent'

export function InstallAgentButton({ agent, name, onInstalled }: { agent: LocalAgentId; name: string; onInstalled?: (detected: DetectedAgent) => void }) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [line, setLine] = useState<string | null>(null)
  const [problem, setProblem] = useState<'npm' | 'failed' | null>(null)

  useEffect(() => {
    if (!busy) return
    let stop: (() => void) | null = null
    let alive = true
    void onAgentInstall((event) => {
      if (event.agent === agent && event.line) setLine(event.line)
    }).then((unlisten) => (alive ? (stop = unlisten) : unlisten()))
    return () => {
      alive = false
      stop?.()
    }
  }, [busy, agent])

  const install = async () => {
    setBusy(true)
    setProblem(null)
    setLine(null)
    try {
      await agentInstall(agent)
      const agents = await detectAgents()
      queryClient.setQueryData(['local-agents'], agents)
      void queryClient.invalidateQueries({ queryKey: ['agent-auth'] })
      const detected = agents.find((a) => a.id === agent)
      if (detected?.installed) onInstalled?.(detected)
    } catch (error) {
      setProblem(String(error).includes('npm-not-installed') ? 'npm' : 'failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <span className="install-agent stack" style={{ gap: 4 }}>
      <button type="button" className="btn btn--sm btn--primary" onClick={() => void install()} disabled={busy}>
        {busy ? <Loader2 size={12} className="spin" /> : <Download size={12} />} {busy ? t('agentInstall.installing', { name }) : t('agentInstall.install', { name })}
      </button>
      {busy && <span className="tiny muted mono truncate install-agent__line" role="status">{line ?? t('agentInstall.starting')}</span>}
      {problem === 'npm' && (
        <span className="tiny" role="alert">
          {t('agentInstall.needsNode', { name })}{' '}
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => void openExternal('https://nodejs.org/')}>{t('agentInstall.getNode')}</button>
        </span>
      )}
      {problem === 'failed' && <span className="tiny" role="alert">{t('agentInstall.failed', { name })}</span>}
    </span>
  )
}
